import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Moon, Search, ShoppingBag, Sun } from 'lucide-react'
import { useCart } from '@/features/cart/cart-context'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { CarouselMark } from './CarouselMark'
import { CategoryDrawer } from './CategoryDrawer'
import { ICON_BUTTON_CLASS } from './icon-button'

interface PublicHeaderProps {
  container?: HTMLDivElement | null
  isDark: boolean
  onToggleDark: () => void
}

export function PublicHeader({ container, isDark, onToggleDark }: PublicHeaderProps) {
  const { itemCount, openCart } = useCart()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [scrolled, setScrolled] = useState(false)

  // El header pasa de "flotante" a "elevado" apenas se hace scroll — un
  // detalle sutil de profundidad que la mayoría de tiendas premium usan,
  // en vez de una sombra fija que se ve pesada cuando la página está arriba.
  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function submitSearch(e: FormEvent) {
    e.preventDefault()
    const term = searchValue.trim()
    navigate(term ? `/?buscar=${encodeURIComponent(term)}` : '/')
    setSearchOpen(false)
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-30 transition-all duration-300',
        scrolled
          ? 'border-b border-border/60 bg-background/80 shadow-[0_4px_24px_-6px_rgba(0,0,0,0.12)] backdrop-blur-xl'
          : 'border-b border-transparent bg-background/60 backdrop-blur-md',
      )}
    >
      {/* Filete decorativo con los 3 colores del isotipo — firma visual de marca, muy sutil. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-primary/0 via-primary/50 to-secondary/0 opacity-80"
      />

      <div className="mx-auto flex min-h-16 max-w-6xl items-center gap-3 px-4 py-2 sm:min-h-[4.5rem] sm:gap-4 sm:px-6 sm:py-3">
        {searchOpen ? (
          <form onSubmit={submitSearch} className="flex flex-1 items-center gap-2 duration-200 animate-in fade-in slide-in-from-top-1">
            <button type="button" onClick={() => setSearchOpen(false)} aria-label="Cerrar búsqueda" className={ICON_BUTTON_CLASS}>
              <ArrowLeft className="size-4.5" />
            </button>
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Buscar productos…"
                className="h-11 rounded-full border-border/60 bg-card/80 pl-10 shadow-sm backdrop-blur-sm focus-visible:border-primary/40 focus-visible:ring-primary/20"
              />
            </div>
          </form>
        ) : (
          <>
            <CategoryDrawer container={container} isDark={isDark} onToggleDark={onToggleDark} />

            <Link
              to="/"
              className="group flex min-w-0 flex-1 items-center gap-2.5 font-heading font-semibold tracking-tight text-foreground"
            >
              <CarouselMark
                size={30}
                className="size-7 shrink-0 transition-transform duration-300 ease-out group-hover:rotate-6 group-hover:scale-110 sm:size-8"
              />
              {/* Nombre completo siempre, en las dos pantallas — en celular se ve
                  más chico y puede ocupar dos líneas si no entra en una. */}
              <span className="text-sm leading-tight sm:text-xl">
                El mágico mundo del{' '}
                <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  carrusel
                </span>{' '}
                de los niños
              </span>
            </Link>

            <button
              type="button"
              onClick={onToggleDark}
              aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
              className={cn(ICON_BUTTON_CLASS, 'hidden sm:flex')}
            >
              {isDark ? <Sun className="size-4.5" /> : <Moon className="size-4.5" />}
            </button>

            <button type="button" onClick={() => setSearchOpen(true)} aria-label="Buscar productos" className={ICON_BUTTON_CLASS}>
              <Search className="size-4.5" />
            </button>

            <button type="button" onClick={openCart} aria-label="Ver tu lista" className={cn(ICON_BUTTON_CLASS, 'relative')}>
              <ShoppingBag className="size-4.5" />
              {itemCount > 0 && (
                <span
                  key={itemCount}
                  className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold tabular-nums text-primary-foreground shadow-sm ring-2 ring-background duration-300 animate-in zoom-in-50"
                >
                  {itemCount > 9 ? '9+' : itemCount}
                </span>
              )}
            </button>
          </>
        )}
      </div>
    </header>
  )
}
